/** Keep in sync with server/api-util/tnSeaticsCustomerErrors.js */
export const TN_TRAFFIC_CUSTOMER_MESSAGE =
  'Uh oh! A temporary traffic error blocked us from securing your tickets for checkout. Please try again.';

const UPSTREAM_TRAFFIC_HTTP = new Set([429, 502, 503, 504]);

/**
 * Match real WSO2/Mercury throttle signals only.
 * Do NOT match every "Ticket Network API" / "Mercury:" string — those appear on
 * ordinary 400 lock failures ("Lock attempt was not successful") and were being
 * rewritten into a fake customer traffic error.
 */
const looksLikeUpstreamThrottleText = text => {
  const s = String(text || '');
  if (!s) {
    return false;
  }
  return (
    /throttl/i.test(s) ||
    /\b900804\b/.test(s) ||
    /exceeded your quota/i.test(s) ||
    /Message throttled out/i.test(s) ||
    /Backend timeout/i.test(s) ||
    /Backend unreachable/i.test(s)
  );
};

/** Transient lock failures the UI should silently retry instead of alerting. */
export const isLockSelectionRetryable = (status, data = {}) => {
  if (isTnUpstreamTrafficError(status, data)) {
    return true;
  }
  const code = Number(status);
  if (code === 502 || code === 503 || code === 504) {
    return true;
  }
  const text = [data?.error, data?.message, data?.description].filter(Boolean).join(' ');
  return /timeout|unreachable|ECONNRESET|ETIMEDOUT|network/i.test(text);
};

export const lockSelectionRetryDelayMs = attempt => {
  // attempt is 1-based for the retry about to start (after a failed try).
  const n = Math.max(1, Number(attempt) || 1);
  return Math.min(8000, 400 * 2 ** (n - 1));
};

export const isTnUpstreamTrafficError = (status, data = {}) => {
  if (data?.code === 'temporarily_unavailable') {
    return true;
  }
  if (UPSTREAM_TRAFFIC_HTTP.has(Number(status))) {
    if (status === 429) {
      return true;
    }
    const errText = data?.error ?? data?.message ?? '';
    if (looksLikeUpstreamThrottleText(errText)) {
      return true;
    }
    if (status === 503 && data?.error === TN_TRAFFIC_CUSTOMER_MESSAGE) {
      return true;
    }
  }
  const combined = [data?.error, data?.message, data?.description].filter(Boolean).join(' ');
  return looksLikeUpstreamThrottleText(combined);
};

export const isFriendlyCheckoutError = err =>
  Boolean(
    err?.isFriendlyCheckoutMessage ||
      err?.message === TN_TRAFFIC_CUSTOMER_MESSAGE ||
      isTnUpstreamTrafficError(err?.status, err?.data)
  );

export const checkoutAlertMessage = (err, { qtyRequiredMessage } = {}) => {
  if (isFriendlyCheckoutError(err)) {
    return err?.message || TN_TRAFFIC_CUSTOMER_MESSAGE;
  }
  if (qtyRequiredMessage && err?.message === qtyRequiredMessage) {
    return err.message;
  }
  return `Checkout error: ${err?.message || 'Something went wrong. Please try again.'}`;
};
